import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Accordion, Card, CardBody, CardHeader, Col, DropdownItem, DropdownMenu, DropdownToggle, Row, UncontrolledDropdown } from 'reactstrap'
import contactProfiles from '../../api/contactProfiles/hooks'
import { ContactProfileService } from '../../api/contactProfiles/types'
import flags from '../../api/flags/hooks'
import { ContactType, HistoryState } from '../../api/globalTypes'
import { OfferEmailSettingsGroup } from '../../api/offers/types'
import { APPLICATION_SETTINGS, MODAL_TYPES } from '../../common/constants'
import { contactProfileViewData } from '../../common/views/contactViewData'
import { showModal } from '../../store/modal'
import { selectCheckedTableRows } from '../../store/table'
import ContactProfileAccordionItem from './ContactProfileAccordionItem'
import ContactProfileTable from './ContactProfileTable'
import { ContactProfileViewTabProps } from './types'

const ContactProfileViewTab = (props: ContactProfileViewTabProps) => {
    const { contactId, contactType, entity, activeTab } = props

    const dispatch = useDispatch()
    const { t } = useTranslation()
    const history = useHistory<HistoryState>()

    const isDemand = activeTab === 'demand'

    const areaUnit = APPLICATION_SETTINGS.AREA_UNIT
    const currency = APPLICATION_SETTINGS.CURRENCY

    const [activeProfileId, setActiveProfileId] = useState(history.location?.state?.profileId ?? '')

    const selectedTableRows = useSelector((state) => selectCheckedTableRows(state))
    
    const { data: featureFlags } = flags.GetFeatureFlags.useQuery()

    const getContactProfilesConfig = {
        params: {
            contactId,
            criteria: {
                contactProfileService: isDemand
                    ? [ContactProfileService.Purchase, ContactProfileService.Lease]
                    : [ContactProfileService.Sale, ContactProfileService.Rent],
            },
        },
    }

    const { data: contactProfilesData } = contactProfiles.GetContactProfiles.useQuery(getContactProfilesConfig.params)

    const contactProfileItems = contactProfilesData?.items ?? []

    const toggleAccordion = (profileId: string) => {
        if (activeProfileId === profileId) {
            setActiveProfileId('')
        } else {
            setActiveProfileId(profileId)
        }
    }

    const handleOfferModal = () => {
        const selectedProperties = selectedTableRows

        // to auto-populate contact in offer modal, extracted in OfferBaseTab selectOptions
        const offerEmailSettingsGroup: OfferEmailSettingsGroup | undefined = entity
            ? {
                  contact_id: entity.id,
                  internalAppId: entity.internalAppId,
                  contact_name:
                      contactType === ContactType.Person
                          ? `${entity.contactPerson?.firstName} ${entity.contactPerson?.lastName}`
                          : (entity.contactCompany?.name ?? ''),
                  contact_email: entity.contactEmail?.[0]?.email ?? '',
                  contact_company_collection:
                      contactType === ContactType.Person
                          ? (entity.contactPersonCompany?.map((cpc) => ({
                                contact_id: cpc.company?.id ?? 0,
                                internalAppId: cpc.company?.internalAppId,
                                contact_name: cpc.company?.contactCompany?.name ?? '',
                                contact_email: '',
                            })) ?? [])
                          : [],
              }
            : undefined

        const payload = {
            modalType: MODAL_TYPES.OFFER_BASE_MODAL,
            modalProps: {
                items: selectedProperties,
                offerType: 'property',
                offerEmailSettingsGroup,
            },
        }

        dispatch(showModal(payload))
    }

    const tableActions =
        isDemand &&
        (() => (
            <div className="d-flex flex-grow-1">
                <UncontrolledDropdown>
                    <DropdownToggle title={t('table.showMore')} href="#" className="btn btn-primary dropdown" tag="button">
                        <i className="mdi mdi-dots-horizontal align-middle"></i>
                    </DropdownToggle>
                    <DropdownMenu className="dropdown-menu-end">
                        <DropdownItem onClick={handleOfferModal} disabled={!selectedTableRows.length}>
                            <i className="mdi mdi-file-document-multiple-outline align-bottom me-2 text-muted"></i> {t('app.common.offerOrPrint')}
                        </DropdownItem>
                    </DropdownMenu>
                </UncontrolledDropdown>
            </div>
        ))

    return (
        <>
            <Row>
                {contactProfileItems.length > 0 && (
                    <Col sm={12} xxl={3}>
                        <Card>
                            <CardHeader>
                                <span className="fw-medium text-primary fs-13">
                                    {isDemand ? t('view.contact.profile.demandTitle') : t('view.contact.profile.offerTitle')}
                                </span>
                            </CardHeader>
                            <CardBody className="pt-2">
                                <Accordion open={activeProfileId} toggle={toggleAccordion}>
                                    {contactProfileItems.map((profile) => {
                                        const profileViewData = isDemand ? contactProfileViewData(profile, { currency, areaUnit, featureFlags }) : []

                                        return (
                                            <ContactProfileAccordionItem
                                                key={profile.id}
                                                item={profile}
                                                contactId={contactId}
                                                profileId={profile.id}
                                                profileViewData={profileViewData}
                                                contactType={contactType}
                                                isDemand={isDemand}
                                                setActiveProfileId={() => setActiveProfileId('')}
                                            />
                                        )
                                    })}
                                </Accordion>
                            </CardBody>
                        </Card>
                    </Col>
                )}
                <Col sm={12} xxl={{ size: 9, offset: contactProfileItems.length > 0 ? 0 : 3 }}>
                    <Card>
                        <CardBody>
                            <Row>
                                <Col>
                                    <ContactProfileTable
                                        key={`${activeProfileId}-${contactProfileItems.length}`}
                                        isDemand={isDemand}
                                        contactId={contactId}
                                        activeProfileId={activeProfileId}
                                        contactProfileItems={contactProfileItems}
                                        toolbarActions={tableActions || undefined}
                                        noResultMessage={
                                            contactProfileItems.length > 0 ? t('table.contactProfile.noResult') : t('app.common.noOptions')
                                        }
                                    />
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </>
    )
}

export default ContactProfileViewTab
